import React, { useState, useMemo } from 'react';
import { 
  FileText, Clock, CheckCircle, XCircle, AlertCircle, 
  Filter, Search, Download, Eye, MessageSquare, Timer,
  User, Mail, Calendar, Zap, BarChart3, ChevronLeft, ChevronRight, ChevronDown
} from 'lucide-react';
import { TaskDetail, AdvancedFilters, FilterPreset, ViewFilterPresets } from '../types/AdminTypes';

interface TasksViewProps {
  taskDetails: TaskDetail[];
  filters: AdvancedFilters;
  setFilters: (filters: AdvancedFilters) => void;
  users: any[];
  filterPresets: ViewFilterPresets;
  activePreset: string;
  onApplyPreset: (preset: FilterPreset) => void;
  onApplyCustomDates: (dateFrom: string, dateTo: string) => void;
}

const TasksView: React.FC<TasksViewProps> = ({ 
  taskDetails, 
  filters, 
  setFilters, 
  users,
  filterPresets,
  activePreset,
  onApplyPreset,
  onApplyCustomDates
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // 📧 États pour le dropdown email rechercheable
  const [emailDropdownOpen, setEmailDropdownOpen] = useState(false);
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  
  // 📄 PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // 📧 Liste unique des emails d'agents
  const uniqueEmails = useMemo(() => {
    const emails = [...new Set(taskDetails.map(task => task.agent_email))].filter(Boolean);
    return emails.sort();
  }, [taskDetails]);

  // 📧 Emails filtrés pour la recherche
  const filteredEmails = useMemo(() => {
    return uniqueEmails.filter(email => 
      email.toLowerCase().includes(emailSearchTerm.toLowerCase())
    );
  }, [uniqueEmails, emailSearchTerm]);

  // Statistiques en temps réel
  const stats = {
    total: taskDetails.length,
    approved: taskDetails.filter(t => t.audit_status === 'approved').length,
    rejected: taskDetails.filter(t => t.audit_status === 'rejected').length,
    pending: taskDetails.filter(t => t.audit_status === 'pending').length,
    withComments: taskDetails.filter(t => t.audit_comment).length,
    avgProcessingTime: taskDetails.filter(t => t.processing_time_seconds).length > 0
      ? Math.round(taskDetails.filter(t => t.processing_time_seconds).reduce((sum, t) => sum + (t.processing_time_seconds || 0), 0) / taskDetails.filter(t => t.processing_time_seconds).length / 60)
      : 0
  };

  // 🔍 FILTRAGE COMPLET avec commentaires
  const { paginatedTasks, totalPages, filteredCount } = useMemo(() => {
    let filtered = taskDetails.filter(task => {
      // Filtre par recherche générale
      const matchesSearch = task.agent_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.task_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.auditor_name && task.auditor_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (task.audit_comment && task.audit_comment.toLowerCase().includes(searchTerm.toLowerCase()));

      // Filtre par email agent spécifique
      const matchesEmail = !filters.agentEmail || task.agent_email === filters.agentEmail;

      // ✅ CORRECTION : Filtre par commentaires
      const matchesComments = !filters.hasComments || (task.audit_comment && task.audit_comment.trim() !== '');

      // Filtre par auditeur
      const matchesAuditor = !filters.auditorId || task.audited_by === filters.auditorId;

      return matchesSearch && matchesEmail && matchesComments && matchesAuditor;
    });

    // Pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    return {
      paginatedTasks: paginated,
      totalPages: Math.ceil(filtered.length / itemsPerPage),
      filteredCount: filtered.length
    };
  }, [taskDetails, searchTerm, currentPage, filters]);

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, taskDetails, filters]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1";
    switch (status) {
      case 'approved': return `${baseClasses} bg-green-100 text-green-800`;
      case 'rejected': return `${baseClasses} bg-red-100 text-red-800`;
      case 'pending': return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default: return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m${remainingSeconds}s`;
  };

  const exportTasks = () => {
    const csv = [
      'Agent Email,Type de Tâche,Statut,Auditeur,Date Audit,Temps Traitement,Commentaire',
      ...taskDetails.map(task => [
        task.agent_email,
        task.task_type,
        task.audit_status,
        task.auditor_name || 'N/A',
        task.audited_at ? new Date(task.audited_at).toLocaleString('fr-FR') : 'N/A',
        task.processing_time_seconds ? formatDuration(task.processing_time_seconds) : 'N/A',
        task.audit_comment ? `"${task.audit_comment.replace(/"/g, '""')}"` : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // 📧 Composant Email Dropdown
  const EmailDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setEmailDropdownOpen(!emailDropdownOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white text-left flex items-center justify-between"
      >
        <span className="truncate">
          {filters.agentEmail || '📧 Tous les emails'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      
      {emailDropdownOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Recherche dans la dropdown */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Rechercher un email..."
              value={emailSearchTerm}
              onChange={(e) => setEmailSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Liste des emails */}
          <div className="max-h-40 overflow-y-auto">
            <button
              onClick={() => {
                setFilters({ ...filters, agentEmail: '' });
                setEmailDropdownOpen(false);
                setEmailSearchTerm('');
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <Mail className="w-4 h-4 text-gray-400" />
              <span>Tous les emails ({uniqueEmails.length})</span>
            </button>
            
            {filteredEmails.map((email) => (
              <button
                key={email}
                onClick={() => {
                  setFilters({ ...filters, agentEmail: email });
                  setEmailDropdownOpen(false);
                  setEmailSearchTerm('');
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 truncate ${
                  filters.agentEmail === email ? 'bg-blue-50 text-blue-700' : ''
                }`}
              >
                {email}
              </button>
            ))}
            
            {filteredEmails.length === 0 && emailSearchTerm && (
              <div className="px-3 py-2 text-sm text-gray-500">
                Aucun email trouvé pour "{emailSearchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const PaginationControls = () => (
    <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
      <div className="flex items-center text-sm text-gray-700">
        <span>
          Affichage {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, filteredCount)} sur {filteredCount} tâches
          {filteredCount !== stats.total && ` (${stats.total} au total)`}
        </span>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Précédent</span>
        </button>
        
        <div className="flex items-center space-x-1">
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = index + 1;
            } else if (currentPage <= 3) {
              pageNum = index + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + index;
            } else {
              pageNum = currentPage - 2 + index;
            }
            
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 border rounded-md text-sm font-medium transition-colors ${
                  currentPage === pageNum
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        
        <button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
        >
          <span>Suivant</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header avec stats en temps réel */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            📋 Détails des Tâches
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            Analyse granulaire • {stats.total} tâches • {filteredCount} affichées
            {filters.hasComments && ` • ${stats.withComments} avec commentaires`}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={exportTasks}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 transition-colors ${
              showFilters ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filtres</span>
            {(filters.agentEmail || filters.auditorId || filters.hasComments) && (
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            )}
          </button>
        </div>
      </div>

      {/* Métriques en temps réel */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs font-medium">Total</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
            <FileText className="w-6 h-6 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs font-medium">Approuvées</p>
              <p className="text-xl font-bold">{stats.approved}</p>
            </div>
            <CheckCircle className="w-6 h-6 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-xs font-medium">Commentées</p>
              <p className="text-xl font-bold">{stats.rejected}</p>
            </div>
            <MessageSquare className="w-6 h-6 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-xs font-medium">En Attente</p>
              <p className="text-xl font-bold">{stats.pending}</p>
            </div>
            <Clock className="w-6 h-6 text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs font-medium">Avec Notes</p>
              <p className="text-xl font-bold">{stats.withComments}</p>
            </div>
            <MessageSquare className="w-6 h-6 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs font-medium">Temps Moy.</p>
              <p className="text-xl font-bold">{stats.avgProcessingTime}min</p>
            </div>
            <Timer className="w-6 h-6 text-orange-200" />
          </div>
        </div>
      </div>

      {/* 🔍 FILTRES AMÉLIORÉS */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📅 Périodes rapides
            </label>
            <div className="flex flex-wrap gap-2">
              {filterPresets.presets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => onApplyPreset(preset)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    activePreset === preset.label
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            {/* 📧 DROPDOWN EMAIL RECHERCHEABLE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Agent</label>
              <EmailDropdown />
            </div>

            {/* 👤 AUDITEUR */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Auditeur</label>
              <select
                value={filters.auditorId}
                onChange={(e) => setFilters({ ...filters, auditorId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">👥 Tous les auditeurs</option>
                {users
                  .filter(user => user && user.role === 'auditor' && user.full_name)
                  .map(auditor => (
                    <option key={auditor.id} value={auditor.id}>
                      {auditor.full_name}
                    </option>
                  ))}
              </select>
            </div>

            {/* ✅ CHECKBOX COMMENTAIRES */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filtres spéciaux</label>
              <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                <input
                  type="checkbox"
                  checked={filters.hasComments}
                  onChange={(e) => setFilters({ ...filters, hasComments: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">💬 Seulement avec commentaires</span>
              </label>
            </div>
          </div>

          {/* Indicateurs de filtres actifs */}
          {(filters.agentEmail || filters.auditorId || filters.hasComments) && (
            <div className="pt-2 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {filters.agentEmail && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    📧 {filters.agentEmail}
                    <button
                      onClick={() => setFilters({ ...filters, agentEmail: '' })}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {filters.auditorId && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    👤 {users.find(u => u.id === filters.auditorId)?.full_name}
                    <button
                      onClick={() => setFilters({ ...filters, auditorId: '' })}
                      className="ml-1 text-purple-600 hover:text-purple-800"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {filters.hasComments && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    💬 Avec commentaires
                    <button
                      onClick={() => setFilters({ ...filters, hasComments: false })}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fermer le dropdown email si on clique ailleurs */}
      {emailDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setEmailDropdownOpen(false)}
        />
      )}

      {/* Barre de recherche */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Rechercher par email, type, auditeur ou commentaire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Reste du code tableau identique... */}
      {/* 📋 TABLEAU AVEC SCROLL ET PAGINATION */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">
              📋 Tâches Détaillées ({filteredCount})
            </h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <BarChart3 className="w-4 h-4" />
              <span>Page {currentPage} sur {totalPages}</span>
            </div>
          </div>
        </div>

        {paginatedTasks.length > 0 ? (
          <>
            {/* 🎯 TABLEAU AVEC SCROLL INTERNE */}
            <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent & Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auditeur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Temps & Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commentaire
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedTasks.map((task, index) => (
                    <tr key={task.task_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">{task.agent_email}</span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <FileText className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{task.task_type}</span>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(task.audit_status)}>
                          {getStatusIcon(task.audit_status)}
                          <span className="capitalize">
                            {task.audit_status === 'approved' ? 'Approuvée' :
                             task.audit_status === 'rejected' ? 'Commentée' : 'En attente'}
                          </span>
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {task.auditor_name ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {task.auditor_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                            <span className="text-sm text-gray-900">{task.auditor_name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Non assigné</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          {task.processing_time_seconds && (
                            <div className="flex items-center space-x-1 text-green-600 mb-1">
                              <Timer className="w-3 h-3" />
                              <span>{formatDuration(task.processing_time_seconds)}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-1 text-gray-500">
                            <Calendar className="w-3 h-3" />
                            <span className="text-xs">
                              {task.audited_at 
                                ? new Date(task.audited_at).toLocaleString('fr-FR')
                                : new Date(task.created_at).toLocaleString('fr-FR')
                              }
                            </span>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        {task.audit_comment ? (
                          <div className="max-w-xs">
                            <p className="text-sm text-gray-900 truncate">{task.audit_comment}</p>
                            {task.audit_comment.length > 50 && (
                              <button
                                onClick={() => setSelectedTask(task)}
                                className="text-xs text-blue-500 hover:text-blue-700"
                              >
                                Voir plus...
                              </button>
                            )}
                          </div>
) : (
    <span className="text-sm text-gray-400">Aucun commentaire</span>
  )}
</td>

<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
  <button
    onClick={() => setSelectedTask(task)}
    className="text-blue-600 hover:text-blue-900 transition-colors"
  >
    <Eye className="w-4 h-4" />
  </button>
</td>
</tr>
))}
</tbody>
</table>
</div>

{/* 📄 CONTRÔLES DE PAGINATION */}
<PaginationControls />
</>
) : (
<div className="text-center py-12">
<FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
<h3 className="text-xl font-bold text-gray-900 mb-2">Aucune tâche trouvée</h3>
<p className="text-gray-600">
{searchTerm 
? `Aucune tâche ne correspond à "${searchTerm}"`
: filters.hasComments 
? 'Aucune tâche avec commentaires trouvée'
: 'Aucune tâche ne correspond aux filtres sélectionnés'
}
</p>
{(filters.agentEmail || filters.auditorId || filters.hasComments) && (
<button
onClick={() => setFilters({ 
...filters, 
agentEmail: '', 
auditorId: '', 
hasComments: false 
})}
className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
>
Réinitialiser les filtres
</button>
)}
</div>
)}
</div>

{/* Modal détail tâche */}
{selectedTask && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
<div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
<div className="p-6 border-b border-gray-200">
<div className="flex items-center justify-between">
<h3 className="text-lg font-bold text-gray-900">Détail de la Tâche</h3>
<button
onClick={() => setSelectedTask(null)}
className="text-gray-400 hover:text-gray-600 text-xl"
>
✕
</button>
</div>
</div>

<div className="p-6 space-y-4">
<div className="grid grid-cols-2 gap-4">
<div>
<label className="block text-sm font-medium text-gray-700">Email Agent</label>
<p className="text-sm text-gray-900 font-mono">{selectedTask.agent_email}</p>
</div>
<div>
<label className="block text-sm font-medium text-gray-700">Type de Tâche</label>
<p className="text-sm text-gray-900">{selectedTask.task_type}</p>
</div>
<div>
<label className="block text-sm font-medium text-gray-700">Statut</label>
<span className={getStatusBadge(selectedTask.audit_status)}>
{getStatusIcon(selectedTask.audit_status)}
<span className="capitalize">
{selectedTask.audit_status === 'approved' ? 'Approuvée' :
 selectedTask.audit_status === 'rejected' ? 'Commentée' : 'En attente'}
</span>
</span>
</div>
<div>
<label className="block text-sm font-medium text-gray-700">Auditeur</label>
<p className="text-sm text-gray-900">{selectedTask.auditor_name || 'Non assigné'}</p>
</div>
</div>

{selectedTask.audit_comment && (
<div>
<label className="block text-sm font-medium text-gray-700 mb-2">Commentaire d'Audit</label>
<div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
<p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedTask.audit_comment}</p>
</div>
</div>
)}

<div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
<div>
<label className="block text-sm font-medium text-gray-700">Créée le</label>
<p className="text-sm text-gray-900">{new Date(selectedTask.created_at).toLocaleString('fr-FR')}</p>
</div>
{selectedTask.audited_at && (
<div>
<label className="block text-sm font-medium text-gray-700">Auditée le</label>
<p className="text-sm text-gray-900">{new Date(selectedTask.audited_at).toLocaleString('fr-FR')}</p>
</div>
)}
{selectedTask.processing_time_seconds && (
<div>
<label className="block text-sm font-medium text-gray-700">Temps de traitement</label>
<p className="text-sm text-gray-900 flex items-center space-x-1">
<Timer className="w-4 h-4 text-green-500" />
<span>{formatDuration(selectedTask.processing_time_seconds)}</span>
</p>
</div>
)}
{selectedTask.session_id && (
<div>
<label className="block text-sm font-medium text-gray-700">ID Session</label>
<p className="text-sm text-gray-900 font-mono break-all">{selectedTask.session_id}</p>
</div>
)}
</div>

{/* Boutons d'action dans la modal */}
<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
<button
onClick={() => setSelectedTask(null)}
className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
>
Fermer
</button>
{selectedTask.audit_comment && (
<button
onClick={() => {
navigator.clipboard.writeText(selectedTask.audit_comment || '');
// Vous pouvez ajouter une notification ici
}}
className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
>
<span>📋</span>
<span>Copier commentaire</span>
</button>
)}
</div>
</div>
</div>
</div>
)}
</div>
);
};

export default TasksView;